import { useState } from 'react';
import NoPhiNotice from './NoPhiNotice';
import { Upload, FileText, ArrowRight, ClipboardList } from 'lucide-react';

interface ProjectCreationWizardProps {
    onClose: () => void;
    onUploadProtocol: (file: File, name?: string, shortName?: string, templateStandard?: string) => void;
    // Both receive the Short Name already typed above. It is one field for the
    // whole wizard, so every path it sits above has to carry it — otherwise the
    // next screen asks again, blank, and the first answer looks rejected.
    onUploadCrfPacket: (shortName?: string) => void;
    onCreateManually: (shortName?: string) => void;
}

export default function ProjectCreationWizard({
    onClose,
    onUploadProtocol,
    onUploadCrfPacket,
    onCreateManually
}: ProjectCreationWizardProps) {
    const [dragActive, setDragActive] = useState(false);
    // Study Short Name for the protocol-upload path. Required, and never
    // derived: it is stamped on every deliverable filename the platform
    // produces. This used to be the first five characters of the filename,
    // uppercased, which is meaningless on a document a sponsor reads.
    const [shortName, setShortName] = useState('');
    const shortNameReady = shortName.trim().length > 0;

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleFileSelection = (file: File) => {
        if (!shortNameReady) return;
        // Skip the review/name step entirely — protocol analysis extracts the
        // real study title from the document and overwrites the project NAME
        // server-side (see protocolAgent.saveAnalysisResults). A placeholder
        // name derived from the filename is passed so the Firestore project
        // doc has *something* non-empty while analysis runs (~3 min).
        //
        // The SHORT NAME is not a placeholder and is not derived. Analysis no
        // longer overwrites it, so whatever is passed here is what ends up on
        // every filename — it has to be the user's own.
        const placeholderName = file.name.replace(/\.(pdf|docx?|doc)$/i, '').replace(/_/g, ' ');
        onUploadProtocol(file, placeholderName, shortName.trim(), 'general');
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (!shortNameReady) return;

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            const allowedExtensions = ['.pdf', '.doc', '.docx'];
            const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            if (allowedTypes.includes(file.type) || allowedExtensions.includes(ext)) {
                handleFileSelection(file);
            }
        }
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelection(e.target.files[0]);
        }
    };


    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            {/* Backdrop with blur */}
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl max-w-4xl w-full overflow-hidden border border-white/20">
                {/* Decorative background elements */}
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative p-8 md:p-12">
                    <div className="text-center mb-10">
                        <h2 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">Create New Study</h2>
                        <p className="text-slate-600 text-lg max-w-xl mx-auto">
                            Upload your protocol or start with an empty project.
                        </p>
                    </div>

                    <div className="max-w-md mx-auto mb-8">
                        <label htmlFor="wizard-short-name" className="block text-sm font-semibold text-slate-800 mb-1">
                            Study Short Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="wizard-short-name"
                            type="text"
                            value={shortName}
                            onChange={(e) => setShortName(e.target.value)}
                            maxLength={40}
                            placeholder="e.g. ONC-2024-001"
                            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-colors"
                        />
                        <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                            Identifies this study on every document the platform produces, including
                            the filename of each one. Letters, numbers, hyphens and underscores; up to
                            40 characters. You can change it later in Study Settings.
                        </p>
                    </div>

                    <div className="max-w-5xl mx-auto mb-6">
                        <NoPhiNotice />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {/* Upload Protocol Option */}
                        <div
                            className={`group relative overflow-hidden rounded-2xl border-2 transition-all duration-300 ${!shortNameReady
                                ? 'opacity-50 cursor-not-allowed border-slate-200 bg-white'
                                : 'cursor-pointer '}${shortNameReady && dragActive
                                ? 'border-primary-500 bg-primary-50/50 scale-[1.02]'
                                : 'border-slate-200 hover:border-primary-400 hover:shadow-xl hover:-translate-y-1 bg-white'
                                }`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => {
                                if (!shortNameReady) return;
                                document.getElementById('file-upload')?.click();
                            }}
                            aria-disabled={!shortNameReady}
                        >
                            <div className="absolute top-0 right-0 bg-gradient-to-l from-primary-600 to-primary-500 text-white text-xs font-bold px-3 py-1 rounded-bl-xl shadow-sm">
                                RECOMMENDED
                            </div>

                            <div className="p-8 flex flex-col items-center text-center h-full">
                                <div className="w-20 h-20 bg-primary-100/50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-inner">
                                    <Upload className="h-10 w-10 text-primary-600" />
                                </div>

                                <h3 className="text-xl font-bold text-slate-900 mb-3">Upload Protocol</h3>
                                <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                                    Drag & drop your protocol document.
                                </p>

                                <div className="mt-auto w-full">
                                    <div className="w-full py-3 bg-primary-600 group-hover:bg-primary-700 text-white rounded-xl text-sm font-semibold shadow-lg shadow-primary-500/20 transition-colors flex items-center justify-center space-x-2">
                                        <span>Select Protocol</span>
                                        <ArrowRight className="w-4 h-4" />
                                    </div>
                                    <p className="text-xs text-slate-400 mt-3 font-medium">
                                        {shortNameReady
                                            ? 'Supports PDF, DOC, DOCX up to 50MB'
                                            : 'Enter a Study Short Name above to continue'}
                                    </p>
                                </div>
                            </div>
                            <input
                                id="file-upload"
                                type="file"
                                className="hidden"
                                accept="application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                onChange={handleFileInput}
                            />
                        </div>

                        {/* Upload CRF Packet Option */}
                        <div
                            className="group relative overflow-hidden rounded-2xl border-2 border-slate-200 bg-white hover:border-primary-400 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                            onClick={() => onUploadCrfPacket(shortName.trim())}
                        >
                            <div className="p-8 flex flex-col items-center text-center h-full">
                                <div className="w-20 h-20 bg-blue-100/60 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-inner">
                                    <ClipboardList className="h-10 w-10 text-blue-600" />
                                </div>

                                <h3 className="text-xl font-bold text-slate-900 mb-3">Upload CRF Packet</h3>
                                <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                                    Already have CRFs? Skip the protocol and import an existing CRF packet.
                                </p>

                                <div className="mt-auto w-full">
                                    <div className="w-full py-3 bg-blue-600 group-hover:bg-blue-700 text-white rounded-xl text-sm font-semibold shadow-lg transition-colors flex items-center justify-center space-x-2">
                                        <span>Select CRF Packet</span>
                                        <ArrowRight className="w-4 h-4" />
                                    </div>
                                    <p className="text-xs text-slate-400 mt-3 font-medium">PDF or DOCX, up to 50MB</p>
                                </div>
                            </div>
                        </div>

                        {/* Create from Scratch Option */}
                        <div
                            className="group relative overflow-hidden rounded-2xl border-2 border-slate-200 bg-white hover:border-slate-400 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                            onClick={() => onCreateManually(shortName.trim())}
                        >
                            <div className="p-8 flex flex-col items-center text-center h-full">
                                <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <FileText className="h-10 w-10 text-slate-600" />
                                </div>

                                <h3 className="text-xl font-bold text-slate-900 mb-3">Manual Setup</h3>
                                <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                                    Start with a blank project. Best for phased studies or when the protocol document isn't ready yet.
                                </p>

                                <div className="mt-auto w-full">
                                    <div className="w-full py-3 bg-slate-800 group-hover:bg-slate-900 text-white rounded-xl text-sm font-semibold shadow-lg transition-colors flex items-center justify-center space-x-2">
                                        <span>Start Empty Project</span>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-3 font-medium">Standard setup flow</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 flex justify-center">
                        <button
                            onClick={onClose}
                            className="text-slate-500 hover:text-slate-800 font-medium text-sm transition-colors px-4 py-2 hover:bg-slate-100 rounded-lg"
                        >
                            Cancel and return to dashboard
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
